import React, { useState, useEffect, useCallback } from "react";
import { useSelector } from "react-redux";
import {
  FiPlus,
  FiSearch,
  FiMapPin,
  FiCalendar,
  FiEdit,
  FiTrash2,
  FiEye,
  FiFileText,
  FiRefreshCw,
  FiCheckCircle,
} from "react-icons/fi";
import { getCustomerLeads, deleteCustomerLead } from "../../../api/customer/customerFeatureApis";
import AddEditCustomerLeadModal from "./AddEditCustomerLeadModal";
import CustomerLeadDetailsModal from "./CustomerLeadDetailsModal";
import "./CustomerLeads.css";

const CustomerLeadsList = () => {
  const authState = useSelector((state) => state.auth);
  const loggedInUser = authState?.user ?? authState;
  const token =
    loggedInUser?.refreshToken ||
    authState?.refreshToken ||
    loggedInUser?.token ||
    authState?.token;

  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");

  const [isAddEditOpen, setIsAddEditOpen] = useState(false);
  const [selectedLeadForEdit, setSelectedLeadForEdit] = useState(null);

  const [isDetailsOpen, setIsDetailsOpen] = useState(false);
  const [selectedLeadForDetails, setSelectedLeadForDetails] = useState(null);

  const [toastMessage, setToastMessage] = useState(null);

  const showToast = (msg) => {
    setToastMessage(msg);
    setTimeout(() => setToastMessage(null), 3500);
  };

  const loadLeads = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    setError(null);
    try {
      const data = await getCustomerLeads(token, {
        page_number: 1,
        page_size: 100,
        order_by: "DESC",
      });
      const list = data?.customerLeads || data?.leads || [];
      setLeads(list);
    } catch (err) {
      setError("Unable to load your leads. Please check your network connection.");
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    loadLeads();
  }, [loadLeads]);

  const handleOpenAdd = () => {
    setSelectedLeadForEdit(null);
    setIsAddEditOpen(true);
  };

  const handleOpenEdit = (lead, e) => {
    e?.stopPropagation();
    setSelectedLeadForEdit(lead);
    setIsAddEditOpen(true);
  };

  const handleOpenDetails = (lead) => {
    setSelectedLeadForDetails(lead);
    setIsDetailsOpen(true);
  };

  const handleDelete = async (lead, e) => {
    e?.stopPropagation();
    if (!window.confirm(`Are you sure you want to delete lead "${lead.title}"?`)) return;

    try {
      const res = await deleteCustomerLead(token, lead.id);
      if (res?.statusType === "SUCCESS" || res?.text) {
        showToast("Lead successfully deleted.");
        setLeads((prev) => prev.filter((l) => l.id !== lead.id));
      } else {
        alert(res?.text || "Failed to delete lead.");
      }
    } catch (err) {
      alert("Failed to delete lead.");
    }
  };

  const handleLeadSaved = (savedLead) => {
    showToast(selectedLeadForEdit ? "Lead updated successfully!" : "Requirement lead posted successfully!");
    loadLeads();
  };

  const filteredLeads = leads.filter((item) => {
    if (!searchQuery) return true;
    const q = searchQuery.toLowerCase();
    const titleMatch = (item.title || "").toLowerCase().includes(q);
    const descMatch = (item.description || "").toLowerCase().includes(q);
    const cityMatch = (item.city || "").toLowerCase().includes(q);
    const stateMatch = (item.state || "").toLowerCase().includes(q);
    const purposeMatch = (item.purpose?.name || item.purpose?.purpose_name || "").toLowerCase().includes(q);
    return titleMatch || descMatch || cityMatch || stateMatch || purposeMatch;
  });

  const formatBudget = (num) => {
    if (!num) return "Price on Request";
    return `₹${Number(num).toLocaleString("en-IN")}`;
  };

  return (
    <div className="customer-leads-root">
      {/* Toast Notification */}
      {toastMessage && (
        <div
          className="position-fixed top-0 end-0 p-3"
          style={{ zIndex: 1100 }}
        >
          <div className="toast show align-items-center text-bg-success border-0 shadow">
            <div className="d-flex">
              <div className="toast-body d-flex align-items-center gap-2">
                <FiCheckCircle size={18} />
                <span>{toastMessage}</span>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Header */}
      <div className="customer-leads-header">
        <div>
          <h1 className="customer-leads-header__title">My Requirement Leads</h1>
          <p className="customer-leads-header__subtitle">
            Manage your verified real estate requirements, track budget parameters, and follow up on notes.
          </p>
        </div>

        <button
          type="button"
          className="btn btn-primary btn-sm px-3 d-inline-flex align-items-center gap-2 shadow-sm"
          onClick={handleOpenAdd}
        >
          <FiPlus size={16} />
          <span>Post Requirement Lead</span>
        </button>
      </div>

      {/* Controls: Search & Refresh */}
      <div className="customer-leads-controls">
        <div className="customer-leads-search">
          <FiSearch size={16} />
          <input
            type="text"
            placeholder="Search leads by title, location, or purpose..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>

        <button
          type="button"
          className="btn btn-outline-secondary btn-sm d-inline-flex align-items-center gap-1"
          onClick={loadLeads}
          disabled={loading}
          title="Refresh leads"
        >
          <FiRefreshCw size={14} className={loading ? "spin" : ""} />
          <span>Refresh</span>
        </button>
      </div>

      {/* Content Area */}
      {loading ? (
        <div className="customer-leads-grid">
          {[1, 2, 3, 4].map((i) => (
            <div key={i} className="customer-lead-card p-4 animate-pulse">
              <div className="d-flex justify-content-between mb-3">
                <div className="bg-light rounded" style={{ width: "65%", height: "20px" }}></div>
                <div className="bg-light rounded-pill" style={{ width: "25%", height: "20px" }}></div>
              </div>
              <div className="bg-light rounded mb-2" style={{ width: "100%", height: "14px" }}></div>
              <div className="bg-light rounded mb-4" style={{ width: "80%", height: "14px" }}></div>
              <div className="d-flex justify-content-between pt-2 border-top">
                <div className="bg-light rounded" style={{ width: "40%", height: "18px" }}></div>
                <div className="bg-light rounded" style={{ width: "30%", height: "18px" }}></div>
              </div>
            </div>
          ))}
        </div>
      ) : error ? (
        <div className="alert alert-danger d-flex align-items-center justify-content-between p-3 rounded-3" role="alert">
          <span>{error}</span>
          <button type="button" className="btn btn-sm btn-outline-danger" onClick={loadLeads}>
            Retry
          </button>
        </div>
      ) : filteredLeads.length === 0 ? (
        <div className="text-center py-5 bg-white rounded-3 border border-slate-200 shadow-sm p-4">
          <div
            className="d-inline-flex align-items-center justify-content-center bg-blue-50 text-primary rounded-circle mb-3"
            style={{ width: "56px", height: "56px" }}
          >
            <FiFileText size={28} />
          </div>
          <h3 className="fs-5 fw-bold text-dark mb-1">
            {searchQuery ? "No matching requirement leads found" : "No requirement leads posted yet"}
          </h3>
          <p className="text-muted small max-w-md mx-auto mb-3">
            {searchQuery
              ? "Try adjusting your search criteria or clear the search filter."
              : "Post your property or project requirements to connect with verified partners and track updates."}
          </p>
          {searchQuery ? (
            <button
              type="button"
              className="btn btn-outline-secondary btn-sm"
              onClick={() => setSearchQuery("")}
            >
              Clear Search
            </button>
          ) : (
            <button
              type="button"
              className="btn btn-primary btn-sm px-4"
              onClick={handleOpenAdd}
            >
              Post First Requirement
            </button>
          )}
        </div>
      ) : (
        <div className="customer-leads-grid">
          {filteredLeads.map((item) => {
            const purposeLabel = item.purpose?.name || item.purpose?.purpose_name || "Requirement";
            const locationStr = [item.city, item.state].filter(Boolean).join(", ");
            const createdDateStr = item.created_on
              ? new Date(item.created_on).toLocaleDateString("en-IN", {
                  day: "numeric",
                  month: "short",
                  year: "numeric",
                })
              : null;

            return (
              <div
                key={item.id}
                className="customer-lead-card"
                onClick={() => handleOpenDetails(item)}
              >
                <div className="customer-lead-card__top">
                  <h3 className="customer-lead-card__title">{item.title}</h3>
                  <span className="customer-lead-card__purpose">{purposeLabel}</span>
                </div>

                <p className="customer-lead-card__desc">
                  {item.description || "No specific details provided."}
                </p>

                <div className="customer-lead-card__meta">
                  {locationStr && (
                    <span className="customer-lead-card__meta-item">
                      <FiMapPin size={13} />
                      <span>{locationStr}</span>
                    </span>
                  )}
                  {createdDateStr && (
                    <span className="customer-lead-card__meta-item">
                      <FiCalendar size={13} />
                      <span>{createdDateStr}</span>
                    </span>
                  )}
                </div>

                <div className="customer-lead-card__footer">
                  <span className="customer-lead-card__budget">{formatBudget(item.budget)}</span>

                  <div className="customer-lead-card__actions">
                    <button
                      type="button"
                      className="btn btn-sm btn-outline-secondary py-1 px-2"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleOpenDetails(item);
                      }}
                      title="View details & notes"
                    >
                      <FiEye size={14} />
                    </button>
                    <button
                      type="button"
                      className="btn btn-sm btn-outline-primary py-1 px-2"
                      onClick={(e) => handleOpenEdit(item, e)}
                      title="Edit lead"
                    >
                      <FiEdit size={14} />
                    </button>
                    <button
                      type="button"
                      className="btn btn-sm btn-outline-danger py-1 px-2"
                      onClick={(e) => handleDelete(item, e)}
                      title="Delete lead"
                    >
                      <FiTrash2 size={14} />
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Add / Edit Modal */}
      <AddEditCustomerLeadModal
        isOpen={isAddEditOpen}
        onClose={() => setIsAddEditOpen(false)}
        onSuccess={handleLeadSaved}
        initialLead={selectedLeadForEdit}
        token={token}
      />

      {/* Details & Notes Modal */}
      <CustomerLeadDetailsModal
        isOpen={isDetailsOpen}
        onClose={() => setIsDetailsOpen(false)}
        lead={selectedLeadForDetails}
        token={token}
        onEditLead={(lead) => {
          setSelectedLeadForEdit(lead);
          setIsAddEditOpen(true);
        }}
      />
    </div>
  );
};

export default CustomerLeadsList;
